import { doc, getDoc, setDoc, onSnapshot, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { applyFooterStyle, type FooterStyle } from "@/lib/footer-style";
import { applyButtonOutline, BUTTON_OUTLINE_DEFAULT } from "@/lib/button-outline";

export interface AppearanceSettings {
  thumbnailRadius: string;   // CSS length, e.g. "0.5rem"
  /** Visitors' footer player background (Super Admin; separate from his own). */
  footerVisitor?: FooterStyle;
  buttonRadius?: string;     // CSS length for --btn-radius, e.g. "0.375rem"
  /** BB's outline thickness for every button, tab and edge button — what
   *  visitors see and what signed-in people get until they pick their own in
   *  Settings → Layout (lib/button-outline.ts). Default "2px", the search
   *  button's (Gordon 2026-11-01). */
  btnOutlineBorderWidth?: string;
  logoUrl?: string;          // App logo shown in sidebar & header
  faviconUrl?: string;       // Browser tab favicon (defaults to logoUrl if not set)
  pwaIconUrl?: string;       // PWA / installed-app icon (192×192 + 512×512 versions)
  appName?: string;          // App name shown next to logo in sidebar
  /** App-wide UI typeface. Any Google Fonts family, or the family name of an
   *  uploaded cloud font (see lib/fonts.ts). Empty = the built-in stack. */
  appFont?: string;
  /** Optional separate typeface for headings. Falls back to appFont. */
  appHeadingFont?: string;
  resendApiKey?: string;     // Resend.com API key for transactional emails (invoicing etc.)
  tapInfoDuration?: number;  // Mobile tap-info tooltip duration in ms (default 2000)
  /** Retired 2026-11-01 — visitors always get the plain dark B&W theme.
   *  Kept only so older settings docs still type-check. */
  guestThemeRotation?: boolean;
  guestThemeRotationSec?: number;
  /** Ordered array of logo image URLs for the sidebar slideshow */
  logoSlideshow?: string[];
  /** Seconds between logo slideshow slides (default 6) */
  logoSlideshowInterval?: number;
  /** Logged-out visitors: replace the top-left logo with a random slideshow of
   *  the app's content thumbnails, with the BB logo re-appearing every 9th
   *  slide. Default true. */
  visitorLogoSlideshow?: boolean;
  /** App Logo Button CMS — which pools feed the thumbnail slideshow. All on
   *  by default. */
  logoSlideshowSources?: { releases?: boolean; artists?: boolean; artistGallery?: boolean; tracks?: boolean };
  /** App Logo Button CMS — the app logo re-appears every N thumbnails
   *  (0 = never show the logo between thumbs). Default 8. */
  logoSlideshowLogoEveryN?: number;
  /** App Logo Button CMS — seconds per THUMBNAIL slide (the content
   *  slideshow; the curated logoSlideshowInterval still governs admin logo
   *  sets). Default 4. */
  logoThumbInterval?: number;
  /** How many tiles wide the open-menu header banner shows (4 or 5).
   *  Header favorites use the header height independently. Default 4. */
  logoBannerTileCount?: number;
  /** Default settings for in-card image slideshows (artist thumbs, release
   *  galleries etc). Per-component props still win, these are the fallbacks.
   *  Values are seconds for human-friendly admin input; SlideshowImage
   *  multiplies by 1000 at the point of use. */
  slideshowIntervalSec?: number;       // default 6
  slideshowFadeSec?: number;           // default 3
  slideshowTransition?:
    | "crossfade" | "fade-black" | "fade-white"
    | "slide-left" | "slide-right" | "slide-up" | "slide-down"
    | "zoom-in" | "zoom-out" | "blur"
    | "ken-burns" | "wipe-left" | "flip" | "swirl";
  /** Animated header ad banner settings */
  adBannerEnabled?: boolean;           // default false (off until explicitly enabled)
  adBannerCycleInterval?: number;      // seconds between ads (default 12)
  adBannerDisplayDuration?: number;    // seconds to hold the expanded ad (default 5)
  adBannerExpandRatio?: number;        // 1–5 squares wide at maximum (default 2)
  adBannerSources?: string[];          // ['marketplace', 'events'] — which collections
  /** VU strip style shown in the thin right-side strip while playing */
  vuStripStyle?: "led" | "waveform" | "spectrum";
  /** Which visualizer panels are on by default when the popup opens */
  vuDefaultMeters?: string[];
  /** Full meter-popup layout defaults per device class, saved by an admin from
   *  inside the popup (Layouts → App default). Applied to users who have no
   *  personal layout yet; a user's own arrangement always wins once they have
   *  one. Shape mirrors the per-user `preferences/vuLayout.current` snapshot
   *  plus activeMeters + meter settings. */
  vuDefaultLayouts?: {
    desktop?: Record<string, unknown>;
    tablet?: Record<string, unknown>;
    mobile?: Record<string, unknown>;
  };
  /** Page-memory policy — which app routes keep scroll position, tab
   *  state and filters across navigations + refresh. Pages NOT listed
   *  here always start fresh on visit (e.g. dashboard, where stale
   *  numbers would mislead). Values are URL path prefixes —
   *  '/artwork-studio' matches both /artwork-studio and any sub-route. */
  rememberedPages?: string[];
  /** Loading screen (BBLogoLoader) — the splash shown between auth resolution
   *  and first paint, and on every route transition. Colors were previously
   *  documented as CSS-variable-configurable in the loader component but
   *  never actually exposed in Super Admin, and never re-applied on page
   *  load for anyone other than the admin mid-edit — so every visitor always
   *  saw the hardcoded default background regardless of what was "saved". */
  loadingScreenCss?: {
    bg?: string;          // CSS color for --bb-loader-bg (default: theme background)
    fg?: string;          // CSS color for --bb-loader-fg (default: theme foreground)
    accent?: string;      // CSS color for --bb-loader-accent (pulse ring + spinner glow)
    pulseCycle?: string;  // e.g. "2.4s"
    spinCycle?: string;   // e.g. "2.6s"
    scale?: string;       // unitless size multiplier for --bb-loader-scale (e.g. "1.2")
  };
  updatedAt?: Timestamp;
  updatedBy?: string;
}

export const DEFAULT_APPEARANCE: AppearanceSettings = {
  thumbnailRadius: "0.25rem",
  btnOutlineBorderWidth: BUTTON_OUTLINE_DEFAULT,
  appName: "Breakin' Bread",
  logoSlideshowInterval: 6,
  logoBannerTileCount: 4,
  slideshowIntervalSec: 6,
  slideshowFadeSec: 3,
  slideshowTransition: "crossfade",
  adBannerCycleInterval: 12,
  adBannerDisplayDuration: 5,
  adBannerExpandRatio: 2,
  adBannerSources: ['marketplace', 'events'],
  // Defaults match Gordon's "Artwork Studio, Film Maker, Media Vault,
  // profile page, DJ" list — these are the workspace-like pages where a
  // scroll reset / lost filter would be costly. Everything else (the
  // dashboard, lists, home) refreshes on visit.
  rememberedPages: [
    '/artwork-studio',
    '/film-maker',
    '/media',
    '/profile',
    '/dj',
    '/songwriter',
    '/upload',
    '/playlist',
  ],
};

/** Size the open-menu header banner tiles to the sidebar's available width.
 *  Sidebar full width is a fixed 16rem (see
 *  SIDEBAR_WIDTH in sidebar.tsx) — assumes the standard 16px root font-size
 *  already relied on elsewhere in this app (e.g. --header-height: 50px). */
// 256 (16rem) minus the banner button's 2px border on each side — matches
// the banner tiles' actual rendered content-box width, not just the
// theoretical sidebar width.
export const SIDEBAR_FULL_WIDTH_PX = 252;
export function menuThumbSizePx(tileCount: number): number {
  const n = tileCount === 5 ? 5 : 4; // only 4 or 5 are offered today
  return Math.round(SIDEBAR_FULL_WIDTH_PX / n);
}

const ref = () => doc(db, "siteSettings", "appearance");

export async function getAppearanceSettings(): Promise<AppearanceSettings> {
  try {
    const snap = await getDoc(ref());
    if (!snap.exists()) return DEFAULT_APPEARANCE;
    return { ...DEFAULT_APPEARANCE, ...snap.data() } as AppearanceSettings;
  } catch {
    return DEFAULT_APPEARANCE;
  }
}

export async function saveAppearanceSettings(
  settings: Partial<AppearanceSettings>,
  userId?: string
): Promise<void> {
  await setDoc(
    ref(),
    {
      ...settings,
      updatedAt: Timestamp.now(),
      ...(userId ? { updatedBy: userId } : {}),
    },
    { merge: true }
  );
}

/** Subscribe to real-time appearance settings changes */
export function subscribeAppearanceSettings(
  callback: (settings: AppearanceSettings) => void
): () => void {
  return onSnapshot(ref(), (snap) => {
    if (snap.exists()) {
      callback({ ...DEFAULT_APPEARANCE, ...snap.data() } as AppearanceSettings);
    } else {
      callback(DEFAULT_APPEARANCE);
    }
  }, () => callback(DEFAULT_APPEARANCE));
}

/** Apply loaded appearance settings to the document root CSS variables */
export function applyAppearanceCss(settings: AppearanceSettings): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (settings.thumbnailRadius) {
    root.style.setProperty("--thumbnail-radius", settings.thumbnailRadius);
  }
  applyFooterStyle("visitor", settings.footerVisitor);
  if (settings.buttonRadius) {
    root.style.setProperty("--btn-radius", settings.buttonRadius);
  }
  applyButtonOutline("visitor", settings.btnOutlineBorderWidth);

  // ── App-wide typeface ────────────────────────────────────────────────────
  // Sets the CSS vars globals.css reads, and loads the family (Google
  // stylesheet or uploaded cloud font) so it actually renders instead of
  // silently falling back. Cleared vars mean "use the built-in stack".
  const applyFontVar = (varName: string, family?: string) => {
    if (family) {
      // Quote the family — names like "Bell Gothic" or "Press Start 2P" are
      // invalid in a CSS font-family list unquoted.
      root.style.setProperty(varName, `"${family.replace(/"/g, "")}"`);
      // Fire-and-forget: never block the paint on a webfont.
      import("@/lib/fonts").then(m => m.ensureFontLoaded(family)).catch(() => {});
    } else {
      root.style.removeProperty(varName);
    }
  };
  applyFontVar("--app-font", settings.appFont);
  applyFontVar("--app-heading-font", settings.appHeadingFont || settings.appFont);
  // Loading screen — MUST run on every load (not just while the admin has the
  // settings panel open) so a real visitor's splash actually reflects what
  // was saved, instead of always falling back to the loader's hardcoded default.
  const lc = settings.loadingScreenCss;
  const loaderVars: [string, string | undefined][] = [
    ["--bb-loader-bg", lc?.bg],
    ["--bb-loader-fg", lc?.fg],
    ["--bb-loader-accent", lc?.accent],
    ["--bb-loader-pulse-sec", lc?.pulseCycle],
    ["--bb-loader-spin-sec", lc?.spinCycle],
    ["--bb-loader-scale", lc?.scale],
  ];
  for (const [name, value] of loaderVars) {
    if (value) root.style.setProperty(name, value);
    else root.style.removeProperty(name); // no saved override -> loader's own default
  }
}

/** Inject or update the favicon <link> tags in the document head */
export function applyFavicon(url: string): void {
  if (typeof document === "undefined" || !url) return;
  const selectors = [
    { rel: "icon", type: "image/png" },
    { rel: "shortcut icon", type: "image/png" },
    { rel: "apple-touch-icon" },
  ];
  selectors.forEach(({ rel, type }) => {
    let el = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement("link");
      el.rel = rel;
      document.head.appendChild(el);
    }
    el.href = url;
    if (type) el.type = type;
  });
}
